"use client";

import React from "react";
import { Icon } from "@iconify/react";
import { Check, Eraser } from "lucide-react";

export interface OptionSelectItem {
  value: string;
  name: string;
}

export interface OptionSelectProps {
  options: OptionSelectItem[];
  label?: string;
  placeholder?: string;
  wrapperClassName?: string;
  value?: string | null;
  onChange?: (value: string | null) => void;
  showClearButton?: boolean;
  className?: string;
}

export default function OptionSelect({
  options,
  label,
  placeholder,
  wrapperClassName,
  value,
  onChange,
  showClearButton = true,
  className,
}: Readonly<OptionSelectProps>) {
  const [isOpen, setIsOpen] = React.useState(false);

  const selectedOption = options.find((item) => item.value === value);
  const borderClass = isOpen ? "border-[#444CE7]" : "border-neutral-300";

  return (
    <div
      className={`relative flex flex-col gap-y-1 w-65 ${
        wrapperClassName || className || ""
      }`}
    >
      {label && <label className="text-base text-neutral-500">{label}</label>}

      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex justify-between items-center p-2 rounded-lg cursor-pointer transition-colors border bg-[#F2F4F7] hover:bg-neutral-50 ${borderClass}`}
        tabIndex={0}
        onKeyDown={() => setIsOpen((prev) => !prev)}
      >
        {selectedOption ? (
          <span>{selectedOption.name}</span>
        ) : (
          <span className="text-neutral-400 text-sm truncate">
            {placeholder}
          </span>
        )}

        <div className="flex items-center gap-2">
          {showClearButton && selectedOption && (
            <Eraser
              size={14}
              className="text-neutral-500 hover:text-neutral-700 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(null);
                setIsOpen(false);
              }}
            />
          )}
          <Icon
            icon="hugeicons:arrow-down-01"
            className={`w-4 h-4 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="relative w-full z-50 p-2 bg-white rounded-lg shadow-sm border border-neutral-200">
          {options.map((item) => {
            const isSelected = item.value === value;
            return (
              <button
                type="button"
                key={item.value}
                onClick={() => {
                  onChange?.(item.value);
                  setIsOpen(false);
                }}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    onChange?.(item.value);
                    setIsOpen(false);
                  }
                }}
                className={`w-full flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                  isSelected
                    ? "bg-brand-700 text-white"
                    : "text-neutral-600 hover:bg-blue-100 hover:text-neutral-900"
                }`}
              >
                <span>{item.name}</span>
                {isSelected && <Check />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
